import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { Header } from '@backstage/core-components';
import 'ag-grid-enterprise';
import Loader from '../loader/Loader';
import {
  Button,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef} from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  menuItem: {
    display: 'block',
    padding: '5px 20px',
  },
});

const UserManagement = () => {
  const identityApi = useApi(identityApiRef);
  const gridRef = useRef<any>(null);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100vh' }), []);
  const gridStyle = useMemo(
    () => ({ height: '100vh', width: '100%', marginTop: '20px' }),
    [],
  );
  const [rowData, setRowdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [userRole, setUserRole] = useState('')
  const [userId, setUserId] = useState('');
  const [updateClick, setUpdateClick] = useState(false);
  const [error, setError] = useState(false);
  const [org, setOrg] = useState('');
  const discoveryApi = useApi(discoveryApiRef);
  const styles = useStyles();
  const fetchApi = useApi(fetchApiRef);

  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('all');
  const [allUsers, setAllUsers] = useState<any[]>([]);

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 

  useEffect(() => {
    const fetchTenantsFromDb = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('user');
        const response = await axios.get(`${baseUrl}/tenants`);
        return response.data;
      } catch (e) {
        console.error('Error fetching tenants:', e);
        return [];
      }
    };

    const fetchData = async () => {
      try {
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        console.log('tenantOrgName------',tenantOrgName)
        if (tenantOrgName) setOrg(tenantOrgName);
        const details = await identityApi.getBackstageIdentity();
        const userRole = details.ownershipEntityRefs.find(ref =>
          ref.startsWith('role:')
        )?.split(':')[1];
        setUserRole(userRole)
        const baseurl = await discoveryApi.getBaseUrl('user');
        const response = await fetchApi.fetch(`${baseurl}`);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API error: ${response.status} - ${errorText}`);
        }

        const allData = await response.json();
        console.log('FetchData response', allData);

        let tenantData = await fetchTenantsFromDb();

        setTenants(tenantData);

        const data = allData.map((user: any) => {
          const tenant = tenantData.find((t: any) => t.id === user.tenantId);
          return {
            ...user,
            tenantName: tenant ? tenant.name : null,
          };
        });

        console.log('data', data);
        setAllUsers(data);
        setRowdata(data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();
    let orgName = localStorage.getItem('orgName');
    if (orgName) {
      setOrg(orgName);
    }
  }, []);

  const ActionsRenderer = ({ data }) => {

    const handleUpdate = async () => {
      setUpdateClick(true);
      setIsDialogOpen(true);
      setUserName(data.name);
      setEmail(data.email);
      setRole(data.role);
      setUserId(data.userId);
    };

    const handleDelete = async () => {
      try{
        if (confirm('Are you sure you want to delete the user ?')) {
          const baseurl = await discoveryApi.getBaseUrl('user');
          const userId = data.userId;
          await axios.delete(`${baseurl}/${userId}`);
          const response = await fetch(`${baseurl}`);
          const res = await response.json();
          setRowdata(res);
        }
      } catch (error) {
        console.log(error, 'error');
      }
     
    };

    return (
      <div>
        <Button
          variant="outlined"
          style={{
            borderRadius: '25px',
            marginRight: '10px',
            height: '32px',
            fontSize: '12px',
          }}
          onClick={handleUpdate}
        >
          Update
        </Button>
        <Button
          variant="outlined"
          style={{ borderRadius: '25px', height: '32px', fontSize: '12px' }}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </div>
    );
  };

  const [columnDefs, setColumnDefs] = useState([
    { headerName: 'Name', field: 'name' },
    { headerName: 'Email', field: 'email' },
    { headerName: 'Role', field: 'role' },
    { headerName: 'Tenant', field: 'tenantName' }, // <-- show tenant
    { headerName: 'Actions', cellRenderer: ActionsRenderer },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      filter: true,
      sortable: true,
      resizable: true,
      enableRowGroup: true,
      tooltipShowDelay: 0,
      tooltipInteraction: true,
      columnMenu: 'new',
    };
  }, []);

  const autoGroupColumnDef = { minWidth: 250 };

  const sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      }
    ],
    defaultToolPanel: '',
  }

  const validateEmail = (email) => {
    // Basic email validation using a regular expression
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleCreate = () => {
    setUpdateClick(false);
    setEmail('');
    setRole('');
    setUserName('');
    setIsDialogOpen(true);
  };

  const handleAddUser = async () => {
    try{
      const baseurl = await discoveryApi.getBaseUrl('user');
      const users = [];
      const user: any = {
        name: userName,
        email: email,
        role: role,
      };
      users.push(user);
      if (userName !== '' && email !== '' && role !== '') {
        await axios.post(`${baseurl}`, users);
        const response = await fetch(`${baseurl}`);
        const data = await response.json();
        setRowdata(data);
      }
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const handleUpdateUser = async () => {
    try {
      const baseurl = await discoveryApi.getBaseUrl('user');
      const user: any = {
        name: userName,
        email: email,
        role: role,
      };
      await axios.put(`${baseurl}/${userId}`, user, {
        headers: { 'Content-Type': 'application/json' },
      });
      const response = await fetch(`${baseurl}`);
      const data = await response.json();
      setRowdata(data);
      setUpdateClick(false);
      setEmail('');
      setUserName('');
      setRole('');
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleFormSubmit = (event: any) => {
    event.preventDefault();
    if (!validateEmail(email)) {
      setError(true);
      return;
    }
    setIsDialogOpen(false);
  };

  const handleTenantChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const value = event.target.value as string;
    setSelectedTenant(value);

    if (value === 'all') {
      setRowdata(allUsers);
    } else {
      setRowdata(allUsers.filter((user) => String(user.tenantId) === value));
    }
  };

  const gridOptions = { suppressContextMenu: true };

  return (
    <div style={containerStyle}>
      <div>
        <Header title="User Management" subtitle={`${org}`} />
      </div>
      {userRole === "SuperAdmin" && (

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
      <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
        <InputLabel id="tenant-select-label"></InputLabel>
          <Select
            labelId="tenant-select-label"
            value={selectedTenant}
            onChange={handleTenantChange}
            displayEmpty
            
            MenuProps={{
              PaperProps: {
                sx: {
                  // force vertical stacking
                  '& .MuiList-root': {
                    display: 'flex !important',
                    flexDirection: 'column !important',
                  },
                  // force left-aligned text inside items
                  '& .MuiMenuItem-root': {
                    justifyContent: 'flex-start',
                    pl: 2,
                  },
                },
              },
            }}
          >
            <MenuItem value="all">All Tenants</MenuItem>
            {tenants.map(tenant => (
              <MenuItem key={tenant.id} value={tenant.id.toString()}>
                {tenant.name}
              </MenuItem>
            ))}
          </Select>

      </FormControl>

      </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="ag-theme-quartz resourcecost" style={gridStyle}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              autoGroupColumnDef={autoGroupColumnDef}
              rowGroupPanelShow={'always'}
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              suppressAggFuncInHeader={true}
              gridOptions={gridOptions}

            />
          </div>
        </div>
      )}

      <div>
        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          sx={{
            '& .MuiDialog-paper': {
              width: '380px',
              maxHeight: '80vh',
            },
          }}
        >
          {!updateClick && (
            <DialogTitle sx={{ borderBottom: '1px solid #ccc' }}>
              Add New User
            </DialogTitle>
          )}
          {updateClick && (
            <DialogTitle sx={{ borderBottom: '1px solid #ccc' }}>
              Update User
            </DialogTitle>
          )}
          <DialogContent>
            <form onSubmit={handleFormSubmit} style={{ width: '300px' }}>
              <TextField
                label="Name"
                name="name"
                value={userName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setUserName(event.target.value);
                }}
                fullWidth
                required
                sx={{ mb: 2, mt: 2 }}
              />
              <TextField
                label="Email"
                name="email"
                value={email}
                error={error}
                helperText={error && 'Please enter a valid email address.'}        
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(event.target.value);
                  setError(!validateEmail(event.target.value));
                }}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth required sx={{ mb: 2 }}>
                <InputLabel id="role-options">Role</InputLabel>
                <Select
                  id="role"
                  value={role}
                  label="Role"
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                    setRole(event.target.value as string);
                  }}
                >
                  <MenuItem className={styles.menuItem} value={'admin'}>admin</MenuItem>
                  <MenuItem className={styles.menuItem} value={'user'}>user</MenuItem>
                </Select>
              </FormControl>
              <DialogActions>
                {
                  !updateClick &&
                  <Button type="submit" variant="contained" color="primary" onClick={handleAddUser}>Create</Button>

                }
                {
                  updateClick &&
                  <Button type="submit" variant="contained" color="primary" onClick={handleUpdateUser}>Update</Button>

                }  <Button onClick={handleCloseDialog} variant="contained" color="secondary">Cancel</Button>
              </DialogActions>
            </form>
          </DialogContent>
        </Dialog>
      </div>

    </div>
  )
}

export default UserManagement;
